import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { View, Alert, ActivityIndicator, Switch, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { LoadingState } from '@/components/ui/LoadingState';
import { useUserStore } from '@/state/userStore';
import { updateSafetyStrictDestinationNews } from '@/services/users';
import { getEmergencyNumberForLocation, makeEmergencyCall } from '@/services/emergency';
import {
  subscribeToTrustedContacts,
  addTrustedContact,
  updateTrustedContact,
  deleteTrustedContact,
  type TrustedContact,
} from '@/services/safety';
import {
  createLocationShare,
  isLocationShareEffective,
  stopLocationShare,
  subscribeToActiveLocationShare,
  subscribeToIncomingLocationShares,
  updateLocationInShare,
  type LocationShare,
} from '@/services/locationSharing';
import { getUserByEmail } from '@/services/collaboration';
import { subscribeToUserTrips, type Trip } from '@/services/trips';
import {
  getCurrentLocation,
  getCurrentLocationForLiveShare,
  watchLocation,
  type LocationCoordinates,
} from '@/services/location';
import { TrustedContactsList } from '@/components/safety/TrustedContactsList';
import { ContactForm } from '@/components/safety/ContactForm';
import { LocationShareModal } from '@/components/safety/LocationShareModal';
import { LiveShareLocationPreview } from '@/components/safety/LiveShareLocationPreview';
import { IncomingLiveSharesMap } from '@/components/safety/IncomingLiveSharesMap';
import { AdvisoriesList, type Advisory } from '@/components/safety/AdvisoriesList';
import {
  subscribeToWeatherAdvisories,
  fetchWeatherAdvisories,
  type WeatherAdvisory,
  type WeatherSubscriptionStatus,
} from '@/services/weather';
import {
  subscribeToMultipleCountryAdvisories,
  fetchTravelAdvisories,
  fetchEntryRequirementAdvisories,
  fetchDestinationNews,
  type TravelAdvisory,
  type AdvisorySubscriptionStatus,
  type DestinationCityMap,
  normalizePlaceText,
  locationMatchesDestination,
  getRegionalSynonymsForDestinations,
  textMatchesRegionalSynonyms,
  textHasTravelImpactSignal,
  filterAdvisoriesForDestinations,
  rankTravelAdvisories,
} from '@/services/travelAdvisories';
import { getEmbassyInfo, normalizeCountryCode, type EmbassyInfo } from '@/services/embassy';
import {
  cacheTravelAdvisories,
  getCachedTravelAdvisories,
  cacheEmbassyInfo,
  getCachedEmbassyInfo,
  cacheTrustedContacts,
  getCachedTrustedContacts,
  cacheWeatherAdvisories,
  getCachedWeatherAdvisories,
  getSafetyOfflineUiState,
  getSafetyOfflineUserPackEnabled,
  setSafetyOfflineUserPackEnabled,
  touchSafetyOfflinePackTimestamp,
  clearSafetyCache,
} from '@/services/offlineSafety';
import { networkMonitor } from '@/services/networkMonitor';
import {
  sendSafetyAdvisoryNotification,
  sendEmergencyContactNotification,
  sendLocationSharingNotification,
} from '@/services/notifications';
import { sendGatedNotification, DEFAULT_COOLDOWNS } from '@/services/notificationGate';
import { buildDedupeKey } from '@/services/notificationDedupe';
import * as Linking from 'expo-linking';
import { OfflineAccessCardShell } from '@/components/offline/OfflineAccessCardShell';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { logger } from '@/services/logger';
import { captureException } from '@/services/errorTracking';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import { isTripCompleted } from '@/utils/tripStatus';

traceModuleEvaluation('app/(tabs)/safety');

type SafetyScreenProps = {
  hideHeader?: boolean;
};

/** Safety advisory local pushes only within this many days before trip start (and while trip is ongoing). */
const ADVISORY_PUSH_LEAD_MS = 7 * 24 * 60 * 60 * 1000;

const SAFETY_STRICT_NEWS_STORAGE_KEY = '@TripFlow/safetyStrictDestinationNews';
const NEWS_MAX_AGE_DAYS = 14;

const shouldSendSafetyAdvisoryPushes = (tripList: Trip[]): boolean => {
  const now = Date.now();
  return tripList.some((trip) => {
    if (trip.isArchived || isTripCompleted(trip)) return false;
    const start = trip.startDate instanceof Date ? trip.startDate : new Date(trip.startDate);
    if (!Number.isFinite(start.getTime())) return false;
    return now >= start.getTime() - ADVISORY_PUSH_LEAD_MS;
  });
};

function SafetyScreenInner({ hideHeader = false }: SafetyScreenProps = {}) {
  const theme = useTheme();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const profile = useUserStore((state) => state.profile);
  const setProfileDetails = useUserStore((state) => state.setProfileDetails);

  // State
  const [trustedContacts, setTrustedContacts] = useState<TrustedContact[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [currentLocation, setCurrentLocation] = useState<LocationCoordinates | null>(null);
  const [emergencyNumber, setEmergencyNumber] = useState<string>('112');
  const [activeLocationShare, setActiveLocationShare] = useState<LocationShare | null>(null);
  const [incomingLocationShares, setIncomingLocationShares] = useState<LocationShare[]>([]);
  /** Bumps periodically so we re-apply share expiration without waiting for a Firestore write. */
  const [locationShareNowTick, setLocationShareNowTick] = useState(() => Date.now());
  const [advisories, setAdvisories] = useState<Advisory[]>([]);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [travelLoading, setTravelLoading] = useState(false);
  const [weatherError, setWeatherError] = useState<string | null>(null);
  const [travelError, setTravelError] = useState<string | null>(null);
  const [activeTripCountryCodes, setActiveTripCountryCodes] = useState<string[]>([]);
  const [embassyInfo, setEmbassyInfo] = useState<EmbassyInfo | null>(null);
  const [loadingEmergency, setLoadingEmergency] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showLocationShareModal, setShowLocationShareModal] = useState(false);
  const [editingContact, setEditingContact] = useState<TrustedContact | null>(null);
  const [safetyDownloaded, setSafetyDownloaded] = useState(false);
  const [downloadingSafety, setDownloadingSafety] = useState(false);
  const [removingSafety, setRemovingSafety] = useState(false);
  const [safetyLastSync, setSafetyLastSync] = useState<number | null>(null);
  const [strictDestinationNews, setStrictDestinationNews] = useState(false);
  const activeOutgoingShareIdRef = useRef<string | null>(null);
  /** Latest trips for push dedupe; kept out of advisory subscription effect deps to avoid resubscribe flicker. */
  const tripsRef = useRef<Trip[]>([]);
  const fetchedWeatherKeyRef = useRef<string | null>(null);
  const fetchedTravelCodesRef = useRef<Set<string>>(new Set());
  const fetchedNewsCodesRef = useRef<Set<string>>(new Set());
  const fetchedNewsKeysRef = useRef<Set<string>>(new Set()); // keys "countryCode" or "countryCode:city" for city-specific
  const fetchedEntryPairsRef = useRef<Set<string>>(new Set());
  const normalizeAdvisoryError = useCallback((message?: string) => {
    if (!message) {
      return 'Advisory service is temporarily unavailable.';
    }
    const lower = message.toLowerCase();
    if (lower.includes('requires an index') || lower.includes('failed-precondition')) {
      return 'Advisory data is still provisioning. Please try again shortly.';
    }
    if (lower.includes('permission') || lower.includes('insufficient')) {
      return 'You do not currently have access to read advisories.';
    }
    return message;
  }, []);

  const advisoriesLoading = weatherLoading || travelLoading;
  const advisoryErrorMessage = weatherError || travelError;

  const tripAdvisoryDestinations = useMemo(() => {
    type DestinationKey = { countryCode: string; city?: string };
    const destinationKeys: DestinationKey[] = [];
    const countryCodeSet = new Set<string>();
    const destinationCitiesByCountry: DestinationCityMap = new Map();

    trips
      .filter((trip) => !trip.isArchived && !isTripCompleted(trip))
      .forEach((trip) => {
        if (trip.destinations?.length) {
          trip.destinations.forEach((destination) => {
            const countrySource = destination.country || destination.countryCode || destination.city;
            if (!countrySource) return;
            const countryCode = normalizeCountryCode(countrySource);
            countryCodeSet.add(countryCode);
            const city = destination.city?.trim() || undefined;
            destinationKeys.push({ countryCode, city });
            const normalizedCity = normalizePlaceText(city);
            if (normalizedCity) {
              const citySet = destinationCitiesByCountry.get(countryCode) ?? new Set<string>();
              citySet.add(normalizedCity);
              destinationCitiesByCountry.set(countryCode, citySet);
            }
          });
        } else if (trip.destination) {
          const parts = trip.destination.split(',').map((part) => part.trim()).filter(Boolean);
          const countryCode = normalizeCountryCode(parts.length > 1 ? parts[parts.length - 1]! : trip.destination);
          const city = parts.length > 1 ? parts[0] : undefined;
          countryCodeSet.add(countryCode);
          destinationKeys.push({ countryCode, city });
          const normalizedCity = normalizePlaceText(city);
          if (normalizedCity) {
            const citySet = destinationCitiesByCountry.get(countryCode) ?? new Set<string>();
            citySet.add(normalizedCity);
            destinationCitiesByCountry.set(countryCode, citySet);
          }
        }
      });

    return {
      countryCodes: Array.from(countryCodeSet),
      destinationCitiesByCountry,
      destinationKeys,
    };
  }, [trips]);

  useEffect(() => {
    const pref = profile.preferences?.safety?.strictDestinationNews;
    if (typeof pref === 'boolean') {
      return;
    }
    let cancelled = false;
    AsyncStorage.getItem(SAFETY_STRICT_NEWS_STORAGE_KEY).then((raw) => {
      if (cancelled || raw === null) return;
      if (raw === 'true') setStrictDestinationNews(true);
      if (raw === 'false') setStrictDestinationNews(false);
    });
    return () => {
      cancelled = true;
    };
  }, [profile.preferences?.safety?.strictDestinationNews]);

  useEffect(() => {
    const pref = profile.preferences?.safety?.strictDestinationNews;
    if (typeof pref === 'boolean') {
      setStrictDestinationNews(pref);
      AsyncStorage.setItem(SAFETY_STRICT_NEWS_STORAGE_KEY, pref ? 'true' : 'false').catch(() => {});
    }
  }, [profile.preferences?.safety?.strictDestinationNews]);

  const handleStrictDestinationNewsChange = useCallback(
    async (value: boolean) => {
      setStrictDestinationNews(value);
      if (firebaseUser?.uid) {
        try {
          await updateSafetyStrictDestinationNews(firebaseUser.uid, value);
        } catch (e) {
          console.warn('[Safety] Could not sync trip-only news preference', e);
        }
      }
      setProfileDetails({
        preferences: {
          ...profile.preferences,
          safety: {
            ...profile.preferences?.safety,
            strictDestinationNews: value,
          },
        },
      });
    },
    [firebaseUser?.uid, profile.preferences, setProfileDetails],
  );

  const newsMatchesDestinationCities = useCallback(
    (advisory: Advisory): boolean => {
      const rawCountry = advisory.location.split(',').pop()?.trim();
      const countryCode = rawCountry ? normalizeCountryCode(rawCountry)?.toUpperCase() : undefined;
      const allDestinationCitySets = Array.from(tripAdvisoryDestinations.destinationCitiesByCountry.values());
      const hasAnyDestinationCities = allDestinationCitySets.some((cities) => cities.size > 0);
      if (!hasAnyDestinationCities) {
        return true;
      }

      const destinationCities =
        countryCode && countryCode.length === 2
          ? tripAdvisoryDestinations.destinationCitiesByCountry.get(countryCode) ?? new Set<string>()
          : new Set<string>(allDestinationCitySets.flatMap((cities) => Array.from(cities)));
      if (destinationCities.size === 0) {
        return true;
      }
      const normalizedTitle = advisory.title.replace(/\s[-|]\s[^-|]{2,80}$/i, '').trim();
      const combinedText = `${normalizedTitle} ${advisory.description || ''}`;

      if (
        locationMatchesDestination(normalizedTitle, destinationCities) ||
        locationMatchesDestination(advisory.description, destinationCities)
      ) {
        return true;
      }
      const regionalSynonyms = getRegionalSynonymsForDestinations(destinationCities);
      if (textMatchesRegionalSynonyms(combinedText, regionalSynonyms)) {
        return true;
      }
      const tripHasThisCountry =
        countryCode && tripAdvisoryDestinations.destinationCitiesByCountry.has(countryCode);
      if (tripHasThisCountry && textHasTravelImpactSignal(combinedText)) {
        return true;
      }
      return false;
    },
    [tripAdvisoryDestinations.destinationCitiesByCountry],
  );

  const freshAdvisories = useMemo(() => {
    const cutoff = Date.now() - NEWS_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
    return advisories.filter((advisory) => {
      if (advisory.type !== 'news') return true;
      const issuedMs =
        advisory.issuedAt instanceof Date ? advisory.issuedAt.getTime() : new Date(advisory.issuedAt).getTime();
      return issuedMs >= cutoff;
    });
  }, [advisories]);

  const displayedAdvisories = useMemo(() => {
    if (!strictDestinationNews) return freshAdvisories;
    return freshAdvisories.filter(
      (a) => a.type !== 'news' || newsMatchesDestinationCities(a),
    );
  }, [freshAdvisories, strictDestinationNews, newsMatchesDestinationCities]);

  /** Avoid header spinner during background refresh when we already have items to show. */
  const showAdvisoriesHeaderSpinner = advisoriesLoading && displayedAdvisories.length === 0;

  const hiddenNewsCount = useMemo(() => {
    if (!strictDestinationNews) return 0;
    const news = freshAdvisories.filter((a) => a.type === 'news');
    return news.filter((a) => !newsMatchesDestinationCities(a)).length;
  }, [freshAdvisories, strictDestinationNews, newsMatchesDestinationCities]);

  const weatherCacheKey = currentLocation
    ? `${currentLocation.latitude.toFixed(2)},${currentLocation.longitude.toFixed(2)}`
    : null;

  const mapTravelTypeToAdvisoryType = useCallback((type: TravelAdvisory['type']): Advisory['type'] => {
    if (type === 'entry_requirements') return 'entry_requirements';
    if (type === 'transport') return 'transport';
    if (type === 'health') return 'health';
    if (type === 'security') return 'security';
    if (type === 'natural_disaster') return 'natural_disaster';
    if (type === 'news') return 'news';
    return 'government';
  }, []);

  const mapTravelSourceLabel = useCallback((source: TravelAdvisory['source']) => {
    if (source === 'step') return 'STEP Program';
    if (source === 'government') return 'Government';
    if (source === 'travelbuddy') return 'TravelBuddy';
    if (source === 'aerodatabox') return 'AeroData';
    if (source === 'gnews') return 'GNews';
    if (source === 'google_news_rss') return 'Google News';
    return 'Manual';
  }, []);
  const isTravelAdvisoryType = useCallback((type: Advisory['type']) => {
    return (
      type === 'government' ||
      type === 'entry_requirements' ||
      type === 'transport' ||
      type === 'health' ||
      type === 'security' ||
      type === 'natural_disaster' ||
      type === 'news'
    );
  }, []);

  tripsRef.current = trips;

  const notifyNewAdvisoryPushes = useCallback((newAdvisories: Advisory[]) => {
      if (!shouldSendSafetyAdvisoryPushes(tripsRef.current)) {
        return;
      }

      const urgent = newAdvisories.filter((a) => a.severity === 'high' || a.severity === 'critical');
      if (urgent.length === 0) return;

      const rank: Record<Advisory['severity'], number> = {
        low: 1,
        medium: 2,
        high: 3,
        critical: 4,
      };
      const maxSeverity = (items: Advisory[]): Advisory['severity'] =>
        items.reduce((best, a) => (rank[a.severity] > rank[best] ? a.severity : best), items[0]!.severity);

      const newsUrgent = urgent.filter((a) => a.type === 'news');
      const nonNewsUrgent = urgent.filter((a) => a.type !== 'news');

      const stableKey = (a: Advisory) => (a.id ? a.id : `${a.type}:${a.title}`.toLowerCase());

      const sendOne = (adv: Advisory) => {
        const advisoryKey = stableKey(adv);
        const priority = adv.severity === 'critical' ? 'high' : 'medium';
        return sendGatedNotification(
          {
            category: 'safety',
            channel: 'push',
            priority,
            dedupeKey: buildDedupeKey.safetyZone(advisoryKey),
            cooldownMs: DEFAULT_COOLDOWNS.safetyZone,
          },
          () => sendSafetyAdvisoryNotification(adv.title, adv.description, adv.severity, adv.id),
        );
      };

      if (nonNewsUrgent.length === 1) {
        void sendOne(nonNewsUrgent[0]!).catch(console.error);
      } else if (nonNewsUrgent.length > 1) {
        const batchKeys = nonNewsUrgent.map(stableKey).sort().join('|');
        const sev = maxSeverity(nonNewsUrgent);
        const priority = sev === 'critical' ? 'high' : 'medium';
        const preview = nonNewsUrgent
          .slice(0, 2)
          .map((a) => a.title.trim())
          .filter(Boolean)
          .join(' · ');
        const body = preview
          ? preview.length > 140
            ? `${preview.slice(0, 137)}… Open Safety for full details.`
            : `${preview}${nonNewsUrgent.length > 2 ? ` (+${nonNewsUrgent.length - 2} more)` : ''}. Open Safety for details.`
          : `${nonNewsUrgent.length} new alerts. Open Safety for details.`;

        void sendGatedNotification(
          {
            category: 'safety',
            channel: 'push',
            priority,
            dedupeKey: buildDedupeKey.safetyAdvisoryBatch(batchKeys),
            cooldownMs: DEFAULT_COOLDOWNS.safetyZone,
          },
          () =>
            sendSafetyAdvisoryNotification(
              `${nonNewsUrgent.length} new safety alerts`,
              body,
              sev,
              undefined,
            ),
        ).catch(console.error);
      }

      if (newsUrgent.length > 0) {
        const sev = maxSeverity(newsUrgent);
        const priority = sev === 'critical' ? 'high' : 'medium';
        const n = newsUrgent.length;
        void sendGatedNotification(
          {
            category: 'safety',
            channel: 'push',
            priority,
            dedupeKey: buildDedupeKey.safetyNewsDigest(),
            cooldownMs: DEFAULT_COOLDOWNS.safetyNewsDigest,
          },
          () =>
            sendSafetyAdvisoryNotification(
              'Destination news updates',
              `${n} new headline${n === 1 ? '' : 's'} for your trip destinations. Open Safety to review.`,
              sev,
              undefined,
            ),
        ).catch(console.error);
      }
  }, []);

  // Subscribe to trusted contacts
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTrustedContacts([]);
      return;
    }

    // Load cached contacts first if offline
    if (!networkMonitor.isOnline()) {
      getCachedTrustedContacts().then((cached) => {
        if (cached.length > 0) {
          setTrustedContacts(cached);
        }
      });
    }

    const unsubscribe = subscribeToTrustedContacts(firebaseUser.uid, async (contacts) => {
      setTrustedContacts(contacts);
      // Cache contacts when online
      if (networkMonitor.isOnline()) {
        await cacheTrustedContacts(contacts);
      }
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid]);

  // Subscribe to trips
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTrips([]);
      return;
    }

    const unsubscribe = subscribeToUserTrips(firebaseUser.uid, (userTrips) => {
      setTrips((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          userTrips.length === 0
        ) {
          return prev;
        }
        return userTrips;
      });
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid]);

  // Check safety offline status (explicit "Download for offline" only — not background cache)
  useEffect(() => {
    const checkSafetyStatus = async () => {
      try {
        const { showAsDownloaded, lastSynced } = await getSafetyOfflineUiState();
        setSafetyDownloaded(showAsDownloaded);
        setSafetyLastSync(lastSynced);
      } catch (error) {
        console.error('[Safety] Error checking offline status:', error);
      }
    };
    checkSafetyStatus();
  }, []);

  // Refresh offline safety cache only when the user opted in (Safety / Offline settings).
  // Otherwise visiting this tab while online would recreate cache after Remove or app restart.
  useEffect(() => {
    let cancelled = false;
    if (!networkMonitor.isOnline() || trips.length === 0) return;

    const refreshPackIfEnabled = async () => {
      try {
        const packEnabled = await getSafetyOfflineUserPackEnabled();
        if (cancelled || !packEnabled) return;

        if (trustedContacts.length > 0) {
          await cacheTrustedContacts(trustedContacts);
        }

        for (const trip of trips) {
          if (trip.destination) {
            // Weather / advisories: filled when dedicated fetch hooks write to this cache
          }
        }

        const { showAsDownloaded, lastSynced } = await getSafetyOfflineUiState();
        if (!cancelled) {
          setSafetyDownloaded(showAsDownloaded);
          setSafetyLastSync(lastSynced);
        }
      } catch (error) {
        console.error('[Safety] Error refreshing offline safety pack:', error);
      }
    };

    void refreshPackIfEnabled();
    return () => {
      cancelled = true;
    };
  }, [trips, trustedContacts, advisories]);

  const handleDownloadSafety = async () => {
    if (!networkMonitor.isOnline()) {
      Alert.alert('Offline', 'You need to be online to download safety information.');
      return;
    }

    setDownloadingSafety(true);
    try {
      await setSafetyOfflineUserPackEnabled(true);

      // Cache all safety data
      if (trustedContacts.length > 0) {
        await cacheTrustedContacts(trustedContacts);
      }

      // Cache advisories
      for (const advisory of advisories) {
        if (advisory.type === 'weather' && advisory.location) {
          // This would need the actual weather advisory data
        }
      }

      await touchSafetyOfflinePackTimestamp();

      const { showAsDownloaded, lastSynced } = await getSafetyOfflineUiState();
      setSafetyDownloaded(showAsDownloaded);
      setSafetyLastSync(lastSynced);
      Alert.alert('Success', 'Safety information downloaded for offline access.');
    } catch (error) {
      console.error('[Safety] Error downloading safety data:', error);
      Alert.alert('Error', 'Failed to download safety information. Please try again.');
    } finally {
      setDownloadingSafety(false);
    }
  };

  const handleRemoveSafetyOffline = () => {
    Alert.alert(
      'Remove offline safety data?',
      'Cached emergency contacts, advisories, and embassy info on this device will be cleared.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            void (async () => {
              setRemovingSafety(true);
              try {
                await clearSafetyCache();
                const next = await getSafetyOfflineUiState();
                setSafetyDownloaded(next.showAsDownloaded);
                setSafetyLastSync(next.lastSynced);
              } catch (e) {
                console.error('[Safety] remove offline', e);
                Alert.alert('Error', 'Could not remove offline safety data.');
              } finally {
                setRemovingSafety(false);
              }
            })();
          },
        },
      ],
    );
  };

  // Subscribe to active location share
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setActiveLocationShare(null);
      return;
    }

    const unsubscribe = subscribeToActiveLocationShare(firebaseUser.uid, (share) => {
      setActiveLocationShare(share);
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid]);

  // Subscribe to shares where this user is a recipient
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setIncomingLocationShares([]);
      return;
    }

    const unsubscribe = subscribeToIncomingLocationShares(firebaseUser.uid, (shares) => {
      setIncomingLocationShares(shares);
    });

    return () => unsubscribe();
  }, [firebaseUser?.uid]);

  // Firestore only re-evaluates expiration on document changes; re-check wall-clock periodically
  // so recipients are not stuck on "Waiting for location" after the session has ended.
  useEffect(() => {
    if (!firebaseUser?.uid) {
      return;
    }
    const id = setInterval(() => setLocationShareNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, [firebaseUser?.uid]);

  useEffect(() => {
    setLocationShareNowTick(Date.now());
  }, [incomingLocationShares, activeLocationShare]);

  const effectiveIncomingLocationShares = useMemo(
    () => incomingLocationShares.filter((s) => isLocationShareEffective(s)),
    [incomingLocationShares, locationShareNowTick],
  );

  const effectiveActiveLocationShare = useMemo(() => {
    if (!isLocationShareEffective(activeLocationShare)) {
      return null;
    }
    return activeLocationShare;
  }, [activeLocationShare, locationShareNowTick]);

  const outgoingLiveShareCoordinate = useMemo((): LocationCoordinates | null => {
    if (!effectiveActiveLocationShare) {
      return null;
    }
    return currentLocation ?? effectiveActiveLocationShare.lastLocation ?? null;
  }, [effectiveActiveLocationShare, currentLocation]);

  useEffect(() => {
    activeOutgoingShareIdRef.current = effectiveActiveLocationShare?.id ?? null;
  }, [effectiveActiveLocationShare?.id]);

  // Firestore already gets lastLocation on create/update, but recipients still need a timely write:
  // watchPosition's first callback is often delayed (especially on real devices / Low Power Mode).
  useEffect(() => {
    if (!effectiveActiveLocationShare || !firebaseUser?.uid) {
      return;
    }
    const shareId = effectiveActiveLocationShare.id;
    let cancelled = false;
    void (async () => {
      try {
        const loc = await getCurrentLocation();
        if (cancelled || !loc) {
          return;
        }
        setCurrentLocation(loc);
        const r = await updateLocationInShare(firebaseUser.uid, loc, shareId);
        if (__DEV__ && !r.success) {
          console.warn('[Safety] Immediate location share write failed:', r.error);
        }
      } catch (error) {
        logger.warn(
          '[Safety] Immediate location share write failed',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveActiveLocationShare?.id, firebaseUser?.uid]);

  // Get current location and emergency number
  useEffect(() => {
    const fetchLocationAndEmergency = async () => {
      setLoadingEmergency(true);
      try {
        const location = await getCurrentLocation();
        setCurrentLocation(location);
        const number = await getEmergencyNumberForLocation(location);
        setEmergencyNumber(number);
      } catch (error) {
        console.error('[Safety] Error getting location:', error);
        setEmergencyNumber('112'); // Fallback
      } finally {
        setLoadingEmergency(false);
      }
    };

    fetchLocationAndEmergency();
  }, []);

  // Get embassy information
  useEffect(() => {
    const fetchEmbassyInfo = async () => {
      if (!profile.citizenship) {
        setEmbassyInfo(null);
        return;
      }

      const countryCode = normalizeCountryCode(profile.citizenship);

      // Try cached first if offline
      if (!networkMonitor.isOnline()) {
        const cached = await getCachedEmbassyInfo(countryCode);
        if (cached) {
          setEmbassyInfo(cached);
          return;
        }
      }

      try {
        const embassy = await getEmbassyInfo(countryCode);
        setEmbassyInfo(embassy);
        // Cache embassy info
        if (embassy && networkMonitor.isOnline()) {
          await cacheEmbassyInfo(countryCode, embassy);
        }
      } catch (error) {
        console.error('[Safety] Error getting embassy info:', error);
        // Fall back to cached if online fetch fails
        const cached = await getCachedEmbassyInfo(countryCode);
        if (cached) {
          setEmbassyInfo(cached);
        } else {
          setEmbassyInfo(null);
        }
      }
    };

    fetchEmbassyInfo();
  }, [profile.citizenship]);

  // Watch location for active share
  useEffect(() => {
    if (!effectiveActiveLocationShare || !firebaseUser?.uid) {
      return;
    }

    const unsubscribe = watchLocation(
      async (location) => {
        try {
          setCurrentLocation(location);
          const sid = activeOutgoingShareIdRef.current;
          await updateLocationInShare(firebaseUser.uid, location, sid ?? undefined);
        } catch (error) {
          logger.warn(
            '[Safety] watchLocation callback failed',
            undefined,
            error instanceof Error ? error : new Error(String(error)),
          );
        }
      },
      {
        timeInterval: 15000,
        distanceInterval: 35,
        omitSimulatorFallback: true,
      }
    );

    return () => unsubscribe();
  }, [effectiveActiveLocationShare, firebaseUser?.uid]);

  // Subscribe to weather advisories
  useEffect(() => {
    if (!currentLocation) {
      setWeatherLoading(false);
      return;
    }

    setWeatherLoading(true);
    setWeatherError(null);

    if (!networkMonitor.isOnline() && weatherCacheKey) {
      getCachedWeatherAdvisories(weatherCacheKey)
        .catch((error) => {
          logger.warn(
            '[Safety] Failed to read cached weather advisories',
            undefined,
            error instanceof Error ? error : new Error(String(error)),
          );
          return [] as Awaited<ReturnType<typeof getCachedWeatherAdvisories>>;
        })
        .then((cachedAdvisories) => {
        if (cachedAdvisories.length === 0) {
          return;
        }

        const converted: Advisory[] = cachedAdvisories.map((adv) => ({
          id: adv.id,
          type: 'weather',
          severity: adv.severity,
          title: adv.title,
          description: adv.description,
          location: adv.location,
          source: adv.source === 'openweather' ? 'OpenWeatherMap' : 'Manual',
          issuedAt: adv.issuedAt,
          expiresAt: adv.expiresAt,
          isRead: adv.isRead,
        }));

        setAdvisories((prev) => [...prev.filter((a) => a.type !== 'weather'), ...converted]);
      });
    }

    if (networkMonitor.isOnline() && weatherCacheKey && fetchedWeatherKeyRef.current !== weatherCacheKey) {
      fetchedWeatherKeyRef.current = weatherCacheKey;
      fetchWeatherAdvisories({
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
      }).then((result) => {
        if (!result.success && result.status) {
          setWeatherError(
            result.status === 'sourceUnavailable'
              ? 'Weather advisory source is currently unavailable.'
              : result.status === 'rateLimited'
                ? 'Weather advisories are being rate-limited. Please try again shortly.'
                : normalizeAdvisoryError(result.error) || 'Could not refresh weather advisories.'
          );
        }
      }).catch((error) => {
        console.error('[Safety] Error triggering weather advisory refresh:', error);
      });
    }

    const unsubscribe = subscribeToWeatherAdvisories(
      { latitude: currentLocation.latitude, longitude: currentLocation.longitude },
      (weatherAdvisories) => {
        setWeatherLoading(false);
        setWeatherError(null);

        if (networkMonitor.isOnline() && weatherCacheKey) {
          cacheWeatherAdvisories(weatherCacheKey, weatherAdvisories).catch((error) => {
            console.error('[Safety] Error caching weather advisories:', error);
          });
        }

        // Convert weather advisories to Advisory format
        const converted: Advisory[] = weatherAdvisories.map((adv) => ({
          id: adv.id,
          type: 'weather',
          severity: adv.severity,
          title: adv.title,
          description: adv.description,
          location: adv.location,
          source: adv.source === 'openweather' ? 'OpenWeatherMap' : 'Manual',
          issuedAt: adv.issuedAt,
          expiresAt: adv.expiresAt,
          isRead: adv.isRead,
        }));

        setAdvisories((prev) => {
          // Notify only for newly seen advisories, but keep the full latest weather advisory set.
          const existingIds = new Set(prev.map((a) => a.id));
          const newAdvisories = converted.filter((a) => !existingIds.has(a.id));
          
          notifyNewAdvisoryPushes(newAdvisories);

          return [...prev.filter((a) => a.type !== 'weather'), ...converted];
        });
      },
      (status: WeatherSubscriptionStatus) => {
        if (status.state === 'error') {
          setWeatherLoading(false);
          setWeatherError(normalizeAdvisoryError(status.message || 'Weather advisories are temporarily unavailable.'));
        }
      }
    );

    return () => unsubscribe();
  }, [currentLocation, weatherCacheKey, normalizeAdvisoryError, notifyNewAdvisoryPushes]);

  // Subscribe to travel advisories for trip destinations
  useEffect(() => {
    const { countryCodes, destinationCitiesByCountry, destinationKeys } = tripAdvisoryDestinations;

    // Dedupe destination keys for news (same country+city only once)
    const seenNewsKeys = new Set<string>();
    const uniqueNewsDestinations = destinationKeys.filter(({ countryCode, city }) => {
      const key = city ? `${countryCode}:${city}` : countryCode;
      if (seenNewsKeys.has(key)) return false;
      seenNewsKeys.add(key);
      return true;
    });

    setActiveTripCountryCodes(countryCodes);
    if (countryCodes.length === 0) {
      setTravelLoading(false);
      setTravelError(null);
      setAdvisories((prev) => prev.filter((a) => !isTravelAdvisoryType(a.type)));
      return;
    }

    setTravelLoading(true);
    setTravelError(null);

    if (networkMonitor.isOnline()) {
      countryCodes.forEach((countryCode) => {
        if (fetchedTravelCodesRef.current.has(countryCode)) {
          return;
        }
        fetchedTravelCodesRef.current.add(countryCode);
        fetchTravelAdvisories(countryCode)
          .then((result) => {
            if (!result.success && result.status === 'sourceUnavailable') {
              setTravelError('Travel advisory source is currently unavailable.');
            }
            if (!result.success && result.status === 'rateLimited') {
              setTravelError('Travel advisories are being rate-limited. Please try again shortly.');
            }
          })
          .catch((error) => {
            console.error('[Safety] Error triggering travel advisory refresh:', error);
          });
      });

      uniqueNewsDestinations.forEach(({ countryCode, city }) => {
        const newsKey = city ? `${countryCode}:${city}` : countryCode;
        if (fetchedNewsKeysRef.current.has(newsKey)) {
          return;
        }
        fetchedNewsKeysRef.current.add(newsKey);
        fetchDestinationNews({ countryCode, city })
          .then((result) => {
            if (!result.success && result.status === 'sourceUnavailable') {
              setTravelError((prev) => prev || 'News source is currently unavailable.');
            }
            if (!result.success && result.status === 'rateLimited') {
              setTravelError((prev) => prev || 'Destination news is being rate-limited. Please try again shortly.');
            }
          })
          .catch((error) => {
            console.error('[Safety] Error triggering destination news refresh:', error);
          });
      });

      if (profile.citizenship) {
        const passportCountry = normalizeCountryCode(profile.citizenship);
        countryCodes.forEach((destinationCountry) => {
          const key = `${passportCountry}_${destinationCountry}`;
          if (fetchedEntryPairsRef.current.has(key)) {
            return;
          }
          fetchedEntryPairsRef.current.add(key);
          fetchEntryRequirementAdvisories({
            passportCountry,
            destinationCountry,
          }).catch((error) => {
            console.error('[Safety] Error triggering entry requirement advisory refresh:', error);
          });
        });
      }
    }

    // Load cached travel advisories first if offline
    if (!networkMonitor.isOnline()) {
      Promise.all(countryCodes.map(code => getCachedTravelAdvisories(code)))
        .catch((error) => {
          logger.warn(
            '[Safety] Failed to read cached travel advisories',
            undefined,
            error instanceof Error ? error : new Error(String(error)),
          );
          return [] as Awaited<ReturnType<typeof getCachedTravelAdvisories>>[];
        })
        .then((cachedArrays) => {
        const allCached = cachedArrays.flat();
        const relevantCached = rankTravelAdvisories(
          filterAdvisoriesForDestinations(allCached, destinationCitiesByCountry),
          destinationCitiesByCountry
        );
        if (allCached.length > 0) {
          const converted: Advisory[] = relevantCached.map((adv) => ({
            id: adv.id,
            type: mapTravelTypeToAdvisoryType(adv.type),
            severity: adv.severity,
            title: adv.title,
            description: adv.description,
            location: adv.locationLabel ?? adv.country,
            source: mapTravelSourceLabel(adv.source),
            issuedAt: adv.issuedAt,
            expiresAt: adv.expiresAt,
            isRead: adv.isRead,
            url: adv.url,
          }));
          setAdvisories((prev) => [...prev.filter((a) => !isTravelAdvisoryType(a.type)), ...converted]);
        }
      });
    }

    const unsubscribe = subscribeToMultipleCountryAdvisories(countryCodes, async (travelAdvisories) => {
      try {
        setTravelLoading(false);
        setTravelError(null);
        const relevantAdvisories = rankTravelAdvisories(
          filterAdvisoriesForDestinations(travelAdvisories, destinationCitiesByCountry),
          destinationCitiesByCountry
        );

        // Cache travel advisories by country
        if (networkMonitor.isOnline()) {
          const advisoriesByCountry = new Map<string, TravelAdvisory[]>();
          relevantAdvisories.forEach(adv => {
            const existing = advisoriesByCountry.get(adv.countryCode) || [];
            existing.push(adv);
            advisoriesByCountry.set(adv.countryCode, existing);
          });

          for (const [countryCode, advisories] of advisoriesByCountry.entries()) {
            await cacheTravelAdvisories(countryCode, advisories);
          }
        }

        // Convert travel advisories to Advisory format
        const converted: Advisory[] = relevantAdvisories.map((adv) => ({
          id: adv.id,
          type: mapTravelTypeToAdvisoryType(adv.type),
          severity: adv.severity,
          title: adv.title,
          description: adv.description,
          location: adv.locationLabel ?? adv.country,
          source: mapTravelSourceLabel(adv.source),
          issuedAt: adv.issuedAt,
          expiresAt: adv.expiresAt,
          isRead: adv.isRead,
          url: adv.url,
        }));

        setAdvisories((prev) => {
          // Notify only for newly seen advisories, but keep the full latest travel advisory set.
          const existingIds = new Set(prev.map((a) => a.id));
          const newAdvisories = converted.filter((a) => !existingIds.has(a.id));

          notifyNewAdvisoryPushes(newAdvisories);

          return [...prev.filter((a) => !isTravelAdvisoryType(a.type)), ...converted];
        });
      } catch (error) {
        logger.warn(
          '[Safety] Travel advisories subscription handler failed',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    },
    (status: AdvisorySubscriptionStatus) => {
      if (status.state === 'error') {
        setTravelLoading(false);
        setTravelError(normalizeAdvisoryError(status.message || 'Travel advisories are temporarily unavailable.'));
      }
    });

    return () => unsubscribe();
  }, [
    tripAdvisoryDestinations,
    normalizeAdvisoryError,
    profile.citizenship,
    mapTravelSourceLabel,
    mapTravelTypeToAdvisoryType,
    isTravelAdvisoryType,
  ]);

  // Handlers
  const handleSOSCall = useCallback(async () => {
    // Notify trusted contacts. NEVER let a notification failure block the
    // emergency call — that's the whole point of this button.
    if (trustedContacts.length > 0 && currentLocation) {
      const primaryContact = trustedContacts.find((c) => c.isPrimary) || trustedContacts[0];
      if (primaryContact) {
        try {
          await sendEmergencyContactNotification(primaryContact.name, {
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
          });
        } catch (notifyError) {
          const err = notifyError instanceof Error ? notifyError : new Error(String(notifyError));
          logger.warn('[Safety] Failed to notify trusted contact during SOS', undefined, err);
          captureException(err, { kind: 'sos_notify_failed' });
        }
      }
    }

    try {
      await makeEmergencyCall(emergencyNumber, true);
    } catch (callError) {
      const err = callError instanceof Error ? callError : new Error(String(callError));
      logger.error('[Safety] Emergency call failed to launch', err);
      captureException(err, { kind: 'sos_call_failed', emergencyNumber });
      Alert.alert(
        'Call could not be placed',
        `We were unable to start the emergency call. Please dial ${emergencyNumber} directly on your phone.`,
      );
    }
  }, [emergencyNumber, trustedContacts, currentLocation]);

  const handleShareLocation = useCallback(() => {
    setShowLocationShareModal(true);
  }, []);

  const handleStartLocationShare = useCallback(
    async (recipients: string[], tripId?: string, expiresInHours?: number) => {
      if (!firebaseUser?.uid) {
        Alert.alert('Error', 'You must be logged in to share location');
        return;
      }

      const normalizedRecipients = Array.from(
        new Set(
          recipients
            .map((recipient) => recipient.trim())
            .filter((recipient) => recipient.length > 0 && recipient !== firebaseUser.uid),
        ),
      );
      if (normalizedRecipients.length === 0) {
        Alert.alert('Error', 'Add at least one trip companion or trusted contact before sharing your location.');
        return;
      }

      // Resolve emails to Firebase UIDs so recipients can read locationShares (rules: uid in sharedWith).
      // Phone-only identifiers stay as-is; those contacts won’t get in-app live map until they use an account-based recipient id.
      const resolvedRecipients: string[] = [];
      for (const r of normalizedRecipients) {
        if (r.includes('@')) {
          if (tripId) {
            const user = await getUserByEmail(r, tripId);
            if (user?.uid) {
              resolvedRecipients.push(user.uid);
            } else {
              resolvedRecipients.push(r);
            }
          } else {
            resolvedRecipients.push(r);
          }
        } else {
          resolvedRecipients.push(r);
        }
      }
      const uniqueRecipients = Array.from(new Set(resolvedRecipients.filter(Boolean)));
      if (uniqueRecipients.length === 0) {
        Alert.alert('Error', 'Add at least one trip companion or trusted contact before sharing your location.');
        return;
      }

      setSharingLocation(true);
      try {
        const expiresAt = expiresInHours
          ? new Date(Date.now() + expiresInHours * 60 * 60 * 1000)
          : undefined;
        const sharerName = profile.name?.trim() || firebaseUser.email?.split('@')[0]?.trim() || undefined;
        const currentShareLocation = await getCurrentLocationForLiveShare();
        if (!currentShareLocation) {
          Alert.alert(
            'Location needed for live sharing',
            'Allow location access and wait for a GPS fix. On the iOS Simulator, use Features → Location → Custom (or a real device)—we do not use a fake default city when sharing with others.',
          );
          return;
        }

        const result = await createLocationShare(
          firebaseUser.uid,
          uniqueRecipients,
          tripId,
          expiresInHours,
          currentShareLocation,
          sharerName,
        );
        if (result.success) {
          setCurrentLocation(currentShareLocation);
          setShowLocationShareModal(false);
          if (result.shareId) {
            const flush = await updateLocationInShare(
              firebaseUser.uid,
              currentShareLocation,
              result.shareId,
            );
            if (__DEV__ && !flush.success) {
              console.warn('[Safety] Post-start location flush failed:', flush.error);
            }
          }
          await sendLocationSharingNotification(true, uniqueRecipients.length, expiresAt);
          Alert.alert('Success', 'Location sharing started');
        } else {
          Alert.alert('Error', result.error || 'Failed to start location sharing');
        }
      } catch (error) {
        Alert.alert('Error', error instanceof Error ? error.message : 'Failed to start location sharing');
      } finally {
        setSharingLocation(false);
      }
    },
    [firebaseUser?.uid]
  );

  const handleStopLocationShare = useCallback(async () => {
    if (!firebaseUser?.uid || !effectiveActiveLocationShare) {
      return;
    }

    const stopSharing = async () => {
      const result = await stopLocationShare(firebaseUser.uid, effectiveActiveLocationShare.id);
      if (result.success) {
        await sendLocationSharingNotification(false, 0);
        Alert.alert('Success', 'Location sharing stopped');
      } else {
        Alert.alert('Error', result.error || 'Failed to stop location sharing');
      }
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.confirm) {
      const confirmed = window.confirm('Stop sharing your location?');
      if (!confirmed) {
        return;
      }
      await stopSharing();
      return;
    }

    Alert.alert(
      'Stop Sharing',
      'Are you sure you want to stop sharing your location?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Stop',
          style: 'destructive',
          onPress: () => {
            void stopSharing();
          },
        },
      ]
    );
  }, [firebaseUser?.uid, effectiveActiveLocationShare]);

  const handleAddContact = useCallback(() => {
    setEditingContact(null);
    setShowContactForm(true);
  }, []);

  const handleEditContact = useCallback((contact: TrustedContact) => {
    setEditingContact(contact);
    setShowContactForm(true);
  }, []);

  const handleSaveContact = useCallback(
    async (contact: Omit<TrustedContact, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (!firebaseUser?.uid) {
        Alert.alert('Error', 'You must be logged in');
        return;
      }

      if (editingContact) {
        const result = await updateTrustedContact(firebaseUser.uid, editingContact.id, contact);
        if (!result.success) {
          throw new Error(result.error || 'Failed to update contact');
        }
      } else {
        const result = await addTrustedContact(firebaseUser.uid, contact);
        if (!result.success) {
          throw new Error(result.error || 'Failed to add contact');
        }
      }
    },
    [firebaseUser?.uid, editingContact]
  );

  const handleDeleteContact = useCallback(
    async (contactId: string) => {
      if (!firebaseUser?.uid) {
        return;
      }

      const result = await deleteTrustedContact(firebaseUser.uid, contactId);
      if (!result.success) {
        Alert.alert('Error', result.error || 'Failed to delete contact');
      }
    },
    [firebaseUser?.uid]
  );

  const handleCallContact = useCallback(async (phoneNumber: string) => {
    const telUrl = `tel:${phoneNumber.replace(/[^\d+]/g, '')}`;
    const canOpen = await Linking.canOpenURL(telUrl);
    if (canOpen) {
      await Linking.openURL(telUrl);
    } else {
      Alert.alert('Error', 'Cannot make phone call');
    }
  }, []);

  const handleAdvisoryPress = useCallback((advisory: Advisory) => {
    if (advisory.type === 'news' && advisory.url && advisory.url.startsWith('https://')) {
      Linking.openURL(advisory.url).catch((err) => {
        console.error('[Safety] Failed to open news URL:', err);
        Alert.alert('Error', 'Could not open article. Please try again.');
      });
      return;
    }
    const issuedText = advisory.issuedAt ? advisory.issuedAt.toLocaleString() : 'Unknown';
    const expiresText = advisory.expiresAt ? advisory.expiresAt.toLocaleString() : 'Not specified';
    Alert.alert(
      advisory.title,
      [
        advisory.description,
        '',
        `Type: ${advisory.type.replace('_', ' ')}`,
        `Severity: ${advisory.severity}`,
        `Location: ${advisory.location}`,
        `Source: ${advisory.source}`,
        `Issued: ${issuedText}`,
        `Expires: ${expiresText}`,
      ].join('\n')
    );
  }, []);

  // Get trip companions count
  const activeTripsWithCompanions = trips.filter((trip) => {
    const now = new Date();
    const endDate = new Date(trip.endDate);
    return endDate >= now && (trip.collaborators?.length || 0) > 0;
  });

  const totalCompanions = activeTripsWithCompanions.reduce((sum, trip) => {
    return sum + (trip.collaborators?.length || 0);
  }, 0);

  const advisoryEmptyMessage = (() => {
    if (advisoriesLoading) {
      return 'Checking weather and travel advisories...';
    }
    if (advisoryErrorMessage) {
      return `Could not load advisories right now. ${advisoryErrorMessage}`;
    }
    if (!currentLocation && activeTripCountryCodes.length === 0) {
      return 'Enable location or add an active/future trip destination to load advisories.';
    }
    if (!currentLocation) {
      return 'Weather advisories need location access. Travel advisories will still appear for your trip destinations.';
    }
    if (activeTripCountryCodes.length === 0) {
      return 'No active or upcoming trip destinations found for travel advisories.';
    }
    if (!networkMonitor.isOnline()) {
      return 'You are offline and no cached advisories are available yet.';
    }
    return 'No weather or travel advisories at this time. Advisories will appear here when available.';
  })();


  return (
    <Screen 
      title={hideHeader ? undefined : "Safety hub"} 
      subtitle={hideHeader ? undefined : "Access local alerts, emergency info, and live location sharing."}
    >
      <OfflineAccessCardShell
        icon="shield-checkmark-outline"
        active={safetyDownloaded}
        title={safetyDownloaded ? 'Available Offline' : 'Download Safety Info'}
        subtitle={
          safetyDownloaded
            ? safetyLastSync
              ? `Last synced ${new Date(safetyLastSync).toLocaleDateString()}`
              : 'Emergency contacts, advisories, and embassy info available offline'
            : 'Download emergency contacts, advisories, and embassy information for offline access'
        }
        trailing={
          !safetyDownloaded ? (
            <Button
              label={downloadingSafety ? 'Downloading...' : 'Download'}
              variant="secondary"
              onPress={handleDownloadSafety}
              disabled={downloadingSafety}
              icon={
                downloadingSafety ? (
                  <ActivityIndicator size="small" color={theme.colors.text} />
                ) : (
                  <Ionicons name="download-outline" size={18} color={theme.colors.text} />
                )
              }
            />
          ) : (
            <>
              <View
                style={{
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.pill,
                  backgroundColor: theme.colors.success + '22',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                  borderWidth: 1,
                  borderColor: (theme.colors.success || '#34C759') + '44',
                }}
              >
                <Ionicons name="checkmark-circle" size={16} color={theme.colors.success || '#34C759'} />
                <Caption style={{ color: theme.colors.success || '#34C759', fontSize: 11, fontWeight: '600' }}>
                  Ready
                </Caption>
              </View>
              <Button
                label={removingSafety ? 'Removing…' : 'Remove'}
                variant="secondary"
                onPress={handleRemoveSafetyOffline}
                disabled={removingSafety || downloadingSafety}
                icon={
                  removingSafety ? (
                    <ActivityIndicator size="small" color={theme.colors.error || '#FF3B30'} />
                  ) : (
                    <Ionicons name="trash-outline" size={18} color={theme.colors.error || '#FF3B30'} />
                  )
                }
              />
            </>
          )
        }
      />

      {/* Rapid Actions */}
      <Card>
        <SubHeading>Rapid actions</SubHeading>
        {effectiveActiveLocationShare ? (
          <LiveShareLocationPreview
            pinId="outgoing-live-share"
            coordinate={outgoingLiveShareCoordinate}
            pinTitle="Your shared location"
            showShareActions
          />
        ) : null}
        <View style={{ gap: theme.spacing(1.5) }}>
          <Button
            label={loadingEmergency ? 'Loading...' : `SOS call (${emergencyNumber})`}
            onPress={handleSOSCall}
            disabled={loadingEmergency}
            icon={
              loadingEmergency ? (
                <ActivityIndicator size="small" color={theme.colors.surface} />
              ) : (
                <Ionicons name="call" size={20} color={theme.colors.surface} />
              )
            }
            accessibilityLabel={loadingEmergency 
              ? "Loading emergency number" 
              : `Emergency call to ${emergencyNumber}`}
            accessibilityHint={loadingEmergency
              ? "Please wait while we determine your local emergency number"
              : `Double tap to call emergency services at ${emergencyNumber}. This will also notify your trusted contacts.`}
            style={{ minHeight: 60 }} // Larger for emergency button
          />
          {effectiveActiveLocationShare ? (
            <Button
              label="Stop sharing location"
              variant="secondary"
              onPress={handleStopLocationShare}
              icon={<Ionicons name="location" size={20} color={theme.colors.primary} />}
              accessibilityLabel="Stop sharing location"
              accessibilityHint="Stops sharing your current location with trusted contacts"
            />
          ) : (
            <Button
              label="Share live location"
              variant="secondary"
              onPress={handleShareLocation}
              icon={<Ionicons name="location-outline" size={20} color={theme.colors.primary} />}
              accessibilityLabel="Share live location"
              accessibilityHint="Share your current location with trusted contacts for safety"
            />
          )}
        </View>
        {effectiveActiveLocationShare && (
          <View style={{ marginTop: theme.spacing(1) }}>
            <Caption color={theme.colors.textSecondary}>
              Sharing with {effectiveActiveLocationShare.sharedWith.length} recipient
              {effectiveActiveLocationShare.sharedWith.length !== 1 ? 's' : ''}
              {effectiveActiveLocationShare.expiresAt && (
                <> • Expires {effectiveActiveLocationShare.expiresAt.toLocaleTimeString()}</>
              )}
            </Caption>
          </View>
        )}
      </Card>

      {effectiveIncomingLocationShares.length > 0 && (
        <Card variant="outlined">
          <SubHeading>Shared with you</SubHeading>
          <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
            One map for everyone sharing with you—each person has a different pin color (see key below).
          </Caption>
          <IncomingLiveSharesMap
            rows={effectiveIncomingLocationShares.map((share) => {
              const trip = share.tripId ? trips.find((t) => t.id === share.tripId) : undefined;
              const collabName = trip?.collaborators?.find((c) => c.userId === share.userId)?.name;
              const tripOwnerName = trip?.owner === share.userId ? trip.ownerName : undefined;
              const sharerLabel =
                share.sharerName?.trim() || collabName || tripOwnerName || 'Shared contact';
              const updatedLabel = share.lastUpdated
                ? `Updated ${share.lastUpdated.toLocaleString()}`
                : 'Waiting for location update…';
              const expiresFragment =
                share.expiresAt != null ? ` • Expires ${share.expiresAt.toLocaleTimeString()}` : '';
              return {
                shareId: share.id,
                label: sharerLabel,
                coordinate: share.lastLocation ?? null,
                statusLine: `${updatedLabel}${expiresFragment}`,
              };
            })}
          />
        </Card>
      )}

      {/* Emergency Services & Embassy */}
      <Card variant="outlined">
        <SubHeading>Emergency contacts</SubHeading>
        <View style={{ gap: theme.spacing(1.5) }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(1),
            }}
          >
            <View style={{ flex: 1, gap: theme.spacing(0.25) }}>
              <ThemedText variant="bodyStrong">Emergency Services</ThemedText>
              <Caption color={theme.colors.textSecondary}>{emergencyNumber}</Caption>
            </View>
            <Button
              label="Call"
              variant="ghost"
              onPress={() => makeEmergencyCall(emergencyNumber, false)}
              icon={<Ionicons name="call-outline" size={16} color={theme.colors.primary} />}
              accessibilityLabel={`Call emergency services at ${emergencyNumber}`}
              accessibilityHint={`Dials ${emergencyNumber} for emergency services`}
            />
          </View>

          {embassyInfo && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: theme.spacing(1),
              }}
            >
              <View style={{ flex: 1, gap: theme.spacing(0.25) }}>
                <ThemedText variant="bodyStrong">
                  {embassyInfo.embassyName}
                </ThemedText>
                <Caption color={theme.colors.textSecondary}>
                  {embassyInfo.emergencyPhone || embassyInfo.phone}
                </Caption>
                {embassyInfo.country && (
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                    {embassyInfo.country}
                  </Caption>
                )}
              </View>
              <Button
                label="Call"
                variant="ghost"
                onPress={() => makeEmergencyCall(embassyInfo.emergencyPhone || embassyInfo.phone, false)}
                accessibilityLabel={`Call embassy at ${embassyInfo.emergencyPhone || embassyInfo.phone}`}
                accessibilityHint={`Dials ${embassyInfo.emergencyPhone || embassyInfo.phone} to contact your embassy`}
                icon={<Ionicons name="call-outline" size={16} color={theme.colors.primary} />}
              />
            </View>
          )}

          {totalCompanions > 0 && (
            <View style={{ padding: theme.spacing(1) }}>
              <ThemedText variant="bodyStrong">Trip Companions</ThemedText>
              <Caption color={theme.colors.textSecondary}>
                {totalCompanions} traveler{totalCompanions !== 1 ? 's' : ''} across{' '}
                {activeTripsWithCompanions.length} trip{activeTripsWithCompanions.length !== 1 ? 's' : ''}
              </Caption>
            </View>
          )}
        </View>
      </Card>

      {/* Trusted Contacts */}
      <Card variant="outlined">
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(1),
          }}
        >
          <SubHeading>Trusted contacts</SubHeading>
          <Button
            label="Add"
            variant="ghost"
            onPress={handleAddContact}
            icon={<Ionicons name="add" size={16} color={theme.colors.primary} />}
          />
        </View>
        <TrustedContactsList
          contacts={trustedContacts}
          onEdit={handleEditContact}
          onDelete={handleDeleteContact}
          onCall={handleCallContact}
        />
      </Card>

      {/* Advisories */}
      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <SubHeading>Advisories</SubHeading>
          {showAdvisoriesHeaderSpinner && (
            <ActivityIndicator size="small" color={theme.colors.textSecondary} />
          )}
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(0.5),
            gap: theme.spacing(1),
          }}
        >
          <View style={{ flex: 1 }}>
            <ThemedText variant="bodyStrong">Trip-only news</ThemedText>
            <Caption color={theme.colors.textSecondary}>
              Hide national stories that are not about your destinations or travel impact (e.g. airport shutdowns still
              show when they match your trip country).
            </Caption>
          </View>
          <Switch
            value={strictDestinationNews}
            onValueChange={handleStrictDestinationNewsChange}
            accessibilityLabel="Trip-only news filter"
          />
        </View>
        <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
          News older than {NEWS_MAX_AGE_DAYS} days is not shown.
        </Caption>
        {strictDestinationNews && hiddenNewsCount > 0 ? (
          <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
            {hiddenNewsCount} news {hiddenNewsCount === 1 ? 'item' : 'items'} hidden — only your destinations, nearby
            regions, or travel disruptions in your destination countries stay visible.
          </Caption>
        ) : null}
        <AdvisoriesList
          advisories={displayedAdvisories}
          onAdvisoryPress={handleAdvisoryPress}
          emptyMessage={advisoryEmptyMessage}
        />
      </Card>

      {/* Modals */}
      <ContactForm
        visible={showContactForm}
        contact={editingContact}
        onClose={() => {
          setShowContactForm(false);
          setEditingContact(null);
        }}
        onSave={handleSaveContact}
      />

      <TripErrorBoundary name="LocationShareModal">
        <LocationShareModal
          visible={showLocationShareModal}
          onClose={() => setShowLocationShareModal(false)}
          onShare={handleStartLocationShare}
          trips={trips}
          trustedContacts={trustedContacts}
          isSharing={sharingLocation}
        />
      </TripErrorBoundary>
    </Screen>
  );
}

export default function SafetyScreen(props: SafetyScreenProps = {}) {
  return (
    <TripErrorBoundary name="SafetyScreen">
      <SafetyScreenInner {...props} />
    </TripErrorBoundary>
  );
}
